import { useState, useRef, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import ReactTooltip from 'react-tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { updateBudgetCategory, BudgetLoadingKeys } from '../../slices/quickbooks/budgetSlice';
import { EditCategoryModal } from './EditCategoryModal';

const ItemTypes = {
    CATEGORY: 'category'
};

export const EditableCategoryName = ({ category, hasEditAccess, index, moveCategory, onDrop, onDeleteClick, onDeleteDisabledClick, onCategoryUpdate }) => {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const containerRef = useRef(null);
    const dragHandleRef = useRef(null);
    const {
        loadingKeys: budgetSettingsLoadingKeys,
    } = useSelector((state) => state.budget);

    const isUpdating = budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.updateBudgetCategory);
    const isDeleting = budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.deleteBudgetCategory);
    
    const budgets = category?.budgets || [];
    const canDelete = budgets.length === 0 && !category?.is_used_in_forecast_settings;

    const [{ isDragging }, drag, preview] = useDrag({
        type: ItemTypes.CATEGORY,
        canDrag: () => hasEditAccess && !isModalOpen,
        item: () => {
            return { index, id: category?.id };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: ItemTypes.CATEGORY,
        hover: (draggedItem) => {
            if (!draggedItem || draggedItem.index === index) return;
            if (isModalOpen) return;
            moveCategory(draggedItem.index, index);
            draggedItem.index = index;
        },
        drop: async (draggedItem) => {
            if (onDrop && draggedItem) {
                await onDrop();
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
    });

    drop(containerRef);
    drag(hasEditAccess && !isModalOpen ? dragHandleRef : containerRef);

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    const handleSave = async (formData) => {
        try {
            await dispatch(updateBudgetCategory({
                categoryId: category.id,
                name: formData.name,
                colors: formData.colors,
            })).unwrap();
            
            setIsModalOpen(false);
            onCategoryUpdate?.();
        } catch (error) {
            console.error(error);
        }
    };

    const showDropIndicator = isOver && canDrop && !isModalOpen;

    if (!hasEditAccess) {
        return (
            <div className="px-4 py-3 bg-white rounded-lg border border-gray-200">
                <h3 className="text-base font-semibold text-gray-900">{category?.name}</h3>
            </div>
        );
    }

    return (
        <div 
            ref={containerRef}
            className={`
                group relative px-4 py-3 bg-white rounded-lg border transition-all duration-200
                ${isDragging ? 'opacity-50 border-blue-300' : 'border-gray-200'}
                ${showDropIndicator ? 'border-blue-500 border-2 shadow-md' : 'hover:border-gray-300 hover:shadow-sm'}
            `}
        >
            <div className="flex items-center justify-between gap-3 py-[0.20rem]">
                {hasEditAccess && (
                    <div 
                        ref={dragHandleRef}
                        className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0"
                        aria-label="Drag to reorder"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </div>
                )}
                <h3 
                    className="text-base font-semibold flex-1"
                    style={{
                        color: category?.colors?.text_color || '#111827',
                        backgroundColor: category?.colors?.bg_color || 'transparent',
                        padding: category?.colors?.bg_color ? '0.25rem 0.5rem' : '0',
                        borderRadius: category?.colors?.bg_color ? '0.25rem' : '0',
                    }}
                >
                    {category?.name}
                </h3>
                {hasEditAccess && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="group-hover:opacity-100 transition-opacity duration-200 p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                            aria-label="Edit category"
                        >
                            <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => {
                                if (canDelete) {
                                    onDeleteClick?.(category);
                                } else {
                                    onDeleteDisabledClick?.(category);
                                }
                            }}
                            disabled={isDeleting}
                            className={`
                                transition-all duration-200 p-1.5 rounded-md
                                ${canDelete 
                                    ? 'text-red-500 hover:text-red-700 hover:bg-red-50' 
                                    : 'text-gray-400 cursor-not-allowed opacity-60'
                                }
                                ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            aria-label={canDelete ? "Delete category" : "Category cannot be deleted"}
                            title={canDelete ? "Delete category" : undefined}
                            data-tip={!canDelete}
                            data-for={`delete-category-tooltip-${category.id}`}
                        >
                            {canDelete ? (
                                <TrashIcon className="h-4 w-4" />
                            ) : (
                                <TrashIcon className="h-4 w-4 text-gray-400" />
                            )}
                        </button>
                        {!canDelete && (
                            <ReactTooltip
                                id={`delete-category-tooltip-${category.id}`}
                                effect="solid"
                                place="top"
                                className="text-xs"
                            >
                                Cannot be deleted. Click for details.
                            </ReactTooltip>
                        )}
                    </div>
                )}
            </div>
            <EditCategoryModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSave}
                isLoading={isUpdating}
                category={category}
            />
        </div>
    );
};
